import { ApolloClient, InMemoryCache, gql } from "@apollo/client";
import { GraphQLErrors } from "@apollo/client/errors";
import { AuthProvider } from "react-admin";
import buildLink from "../client/link";

export const getCsrfToken = () => localStorage.getItem("csrfToken");
export const setCsrfToken = (token: string) =>
  localStorage.setItem("csrfToken", token);
export const removeCsrfToken = () => localStorage.removeItem("csrfToken");
export const getRole = () => localStorage.getItem("role");
export const setRole = (role: string) => localStorage.setItem("role", role);
export const removeRole = () => localStorage.removeItem("role");

const link = buildLink({ getCsrfToken, setCsrfToken });
const client = new ApolloClient({ link, cache: new InMemoryCache() });

const authProvider: AuthProvider = {
  login: ({ username, password }: { username: string; password: string }) => {
    return client
      .mutate({
        mutation: gql`
          mutation Login($credentials: LoginCredentials!) {
            login(credentials: $credentials) {
              account {
                id
                role
              }
              csrfToken
              error {
                code
              }
            }
          }
        `,
        variables: {
          credentials: {
            emailAddress: username,
            password,
          },
        },
      })
      .then(({ data }) => {
        if (data.login.error) {
          return Promise.reject(data.login.error.code);
        }
        const csrfToken = data.login.csrfToken;
        const role = data.login.account.role;

        setCsrfToken(csrfToken);
        setRole(role);
      });
  },
  logout: () => {
    return client
      .mutate({
        mutation: gql`
          mutation Logout {
            logout {
              code
            }
          }
        `,
      })
      .then(({ data }) => {
        if (data.logout?.code) {
          console.warn("Error on logout", data.logout.code);
        }
        return Promise.resolve();
      })
      .catch((error) => {
        console.warn("Error on logout", error);
        return Promise.resolve();
      })
      .finally(() => {
        // Remove CSRF token and role anyway to enable re-login
        removeCsrfToken();
        removeRole();
      });
  },
  checkAuth: () => {
    const csrfToken = getCsrfToken();
    if (!csrfToken) {
      return Promise.reject();
    }
    return Promise.resolve();
  },
  checkError: (error: any) => {
    // Note: this needs a patched version of ra-data-graphql that provides the original ApolloError in the body
    const graphQLErrors = error?.body?.graphQLErrors as
      | GraphQLErrors
      | undefined;
    if (graphQLErrors && graphQLErrors.length > 0) {
      for (let graphQLError of graphQLErrors) {
        const errorType = graphQLError?.extensions?.type;
        if (
          errorType === "authenticationRequired" ||
          errorType === "authTokenExpired" ||
          errorType === "authTokenInvalid" ||
          errorType === "csrfTokenMissing" ||
          errorType === "csrfTokenInvalid"
        ) {
          removeCsrfToken();
          removeRole();
          return Promise.reject();
        }
      }
    }
    return Promise.resolve();
  },
  getPermissions: () => {
    const role = getRole() || "Anonymous";
    return Promise.resolve(role);
  },
};

export default authProvider;
